import React, { useCallback, useState } from "react";
import { useContextSelector } from "use-context-selector";
import { SerializedStyles } from "@emotion/react";
import Dropzone from "react-dropzone";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import bluebird from "bluebird";
import axios from "axios";

import {
  Banner,
  Button,
  Flex,
  Panel,
  Spinner,
  FileUpload,
  Label,
  theme,
} from "@xometry/xometry_loft";

import { PfsFile, UnitOfMeasurement } from "../../schema/generated/schema";

import fileUtils, { FileEntity } from "../../utils/fileUtils";

import UserContext from "../proposals/contexts/userContext";

import Modals from "../proposals/Modals";
import FileList from "./FileList";
import {
  addFilesPanel,
  container,
  HeaderSection,
  Section,
} from "./FileShare.style";
import { JsonSchemaDataContext } from "../JsonSchemaLoftTheme/JsonSchemaDataContext";
import FancyDownload from "./FancyDownload";
import FileContext from "../proposals/contexts/fileContext";
import { useConfig } from "../proposals/contexts/configContext";

const shouldShowFileUnitModal = (files: File[]) => {
  return files.some(file => fileUtils.fileNeedUserInputForUnits(file.name));
};

interface FileShareWrapperProps {
  disabled: boolean;
  headerNeeded?: boolean;
  fileShareEntity: FileEntity;
  inputFileId?: string;
  visible?: boolean;
  loading?: boolean;
  shouldWrapOnPanel?: boolean;
  containerCss?: SerializedStyles;
  onFinish?: (filesData: PfsFile[] | undefined) => void;
  accept?: string | string[];
  showLabel?: boolean;
  customStyling?: boolean;
  style?: any;
}

const FileShare: React.FunctionComponent<FileShareWrapperProps> = ({
  disabled,
  headerNeeded,
  fileShareEntity,
  children,
  inputFileId,
  visible = true,
  shouldWrapOnPanel = true,
  containerCss,
  onFinish,
  accept,
  showLabel,
  customStyling,
  style,
}) => {
  const proposal = useContextSelector(
    JsonSchemaDataContext,
    context => context.valueModel
  );
  const files = useContextSelector(FileContext, ctx => ctx.data.files);
  const guestUpload = useContextSelector(
    FileContext,
    ctx => ctx.data.guestUpload
  );
  const contextErrors = useContextSelector(
    FileContext,
    ctx => ctx.data.errMessage
  );
  const addFiles = useContextSelector(FileContext, ctx => ctx.actions.addFiles);
  const filesLoading = useContextSelector(FileContext, ctx => ctx.data.loading);
  const dismissErrors = useContextSelector(
    FileContext,
    ctx => ctx.actions.dismissErrors
  );

  const loggedInUserId = useContextSelector(
    UserContext,
    ctx => ctx.loggedInUser?.id || ""
  );

  const { origins, authHeaders } = useConfig();

  const [errMessage, setErrorMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(null as number | null);
  const [fileUnitModalOpen, setFileUnitModalOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<any[]>([]);

  const resetState = () => {
    setFileUnitModalOpen(false);
    setFilesToUpload([]);
    setDownloading(false);
  };

  const loadingContent =
    loadingPercent !== null && !filesLoading ? (
      <FancyDownload percentComplete={loadingPercent} />
    ) : (
      <Spinner scale="panel" />
    );

  const handleDownloadAllFiles = async (e, fileList: readonly PfsFile[]) => {
    e.preventDefault();
    e.stopPropagation();
    const zip = new JSZip();
    const totalSize = fileList.reduce(
      (sum, file) => sum + (file.data?.size || 1000000),
      0
    );
    const reasonableInternetSpeed = 250000; // per half second
    let sizeComplete = 0;
    setLoadingPercent(0);
    await bluebird.map(
      fileList,
      async file => {
        const fileName = fileUtils.getOriginalFilename(file);
        const downloadUrl = fileUtils.buildDownloadUrl(origins, file);
        const size = file.data?.size || 1000000;
        const endPoint = sizeComplete + size;
        const interval = setInterval(() => {
          sizeComplete = Math.min(
            sizeComplete + reasonableInternetSpeed,
            endPoint
          );
          if (sizeComplete >= endPoint) {
            clearInterval(interval);
          }
          setLoadingPercent(Math.round((sizeComplete / totalSize) * 99));
        }, 500);
        const { data, status } = await axios.get(downloadUrl, {
          responseType: "blob",
          headers: {
            ...authHeaders,
          },
        });
        if (status !== 200) {
          throw new Error(`pfs error status: ${status}, body: ${data}`);
        }
        zip.file(fileName, data);
        sizeComplete = endPoint;
        clearInterval(interval);
      },
      { concurrency: 1 }
    );
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `${proposal?.refId || "out"}.zip`);
    setLoadingPercent(null);
  };

  const doUpload = useCallback(
    async (
      files: File[],
      fileUnits: Record<string, UnitOfMeasurement> = {}
    ) => {
      setFileUnitModalOpen(false);

      try {
        const filesData = await addFiles(
          files,
          fileShareEntity,
          loggedInUserId,
          proposal?.buyerUserId || "",
          proposal?.sellerPartnerId || "",
          fileUnits
        );
        onFinish?.(filesData);
      } catch (err: any) {
        setErrorMessage(
          `There was an unexpected error while uploading files: ${err.message}`
        );
      }

      resetState();
    },
    [fileShareEntity, proposal, loggedInUserId]
  );

  const handleDrop = async (accepted, rejected) => {
    setLoadingPercent(null);
    const rejectedFiles = rejected.map(file => file.file.name).join(", ");

    if (rejectedFiles) {
      setErrorMessage(`these files were rejected: ${rejectedFiles}`);
      return;
    }

    setDownloading(true);

    setFilesToUpload(accepted);
    if (!shouldShowFileUnitModal(accepted)) {
      doUpload(accepted);
    } else {
      setFileUnitModalOpen(true);
    }
  };

  if (disabled && files.length === 0) {
    return null;
  }

  const applyCustomStyle = customStyling && {
    background: "white",
    padding: "20px",
  };
  const ApplyWrapOnPanel: React.FunctionComponent = ({ children }) => {
    if (shouldWrapOnPanel) {
      return (
        <Panel kind="primary" style={style} css={Section}>
          {children}
        </Panel>
      );
    }
    return <>{children}</>;
  };

  return (
    <div
      style={{
        display: visible ? "block" : "none",
        ...applyCustomStyle,
      }}
    >
      <ApplyWrapOnPanel>
        {showLabel && <Label>Project Files</Label>}
        {headerNeeded && (
          <div css={HeaderSection}>
            <Flex flexDirection="column">
              <h3 className="title">Project Files</h3>
              {!disabled && (
                <div className="subtitle">
                  Upload any relevant documents to be shared for this project.
                </div>
              )}
            </Flex>
            {!downloading && files.length > 0 && !guestUpload && (
              <Button
                kind="text-blue"
                onClick={async event => {
                  setDownloading(true);
                  try {
                    await handleDownloadAllFiles(event, files);
                  } catch (err) {
                    setErrorMessage("There was an error zipping files");
                    setDownloading(false);
                    throw err;
                  }
                  setDownloading(false);
                }}
                className="downloadAllLink"
              >
                <i className="fas fa-download" />
                Download File(s)
              </Button>
            )}
          </div>
        )}
        {!!(errMessage || contextErrors) && (
          <Banner
            kind={theme.kind.danger}
            title="Error"
            buttonText="Dismiss"
            onButtonClick={() => {
              dismissErrors();
              setErrorMessage("");
            }}
          >
            {errMessage || contextErrors}
          </Banner>
        )}
        {downloading || filesLoading ? (
          loadingContent
        ) : (
          <Flex flexDirection="column">
            {children ? (
              <Dropzone
                disabled={disabled}
                onDrop={handleDrop}
                maxSize={1024 * 1024 * 100}
                multiple
                accept={accept}
              >
                {({ getRootProps, getInputProps }) => (
                  <div css={addFilesPanel} {...getRootProps()}>
                    <input
                      hidden
                      {...getInputProps()}
                      {...(inputFileId && { id: inputFileId })}
                    />
                    <div css={[container, containerCss]}>
                      <Flex flexDirection="column" alignItems="center">
                        {children}
                      </Flex>
                    </div>
                  </div>
                )}
              </Dropzone>
            ) : (
              <FileUpload
                fileList={filesToUpload}
                onFileSelected={files => {
                  handleDrop(
                    files.map(file => file.clientFile),
                    []
                  );
                }}
              />
            )}
            <FileList disabled={disabled} disableLink={guestUpload} />
          </Flex>
        )}
        <Modals.FileUnitModal
          files={filesToUpload}
          modalOpen={fileUnitModalOpen}
          closeModal={resetState}
          onSubmit={fileUnits => doUpload(filesToUpload, fileUnits)}
        />
      </ApplyWrapOnPanel>
    </div>
  );
};

FileShare.defaultProps = {
  headerNeeded: false,
  inputFileId: undefined,
  visible: true,
  loading: false,
  shouldWrapOnPanel: true,
  containerCss: undefined,
  onFinish: undefined,
  accept: undefined,
  showLabel: undefined,
  customStyling: undefined,
  style: undefined,
};

export default FileShare;
